import { AppDispatch, GlobalState } from '../../../../store'
import { Paper } from '../../../common/Form'
import { AdBreak, AdCreativeWithUrls, AdPod } from 'common/api/v1/types'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { Pagination, TableConfig } from '../../../common/Table'
import MuiTable from '@mui/material/Table'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import { useSelector } from 'react-redux'
import { equals } from 'common/util'
import { Fragment, ReactNode, useEffect, useState } from 'react'
import { getAdAudit } from '../../../../redux/actions/adAuditActions'
import { AdAuditEntity, AdAuditLogMessage, AdAuditMethod, AdAuditStatus, AdServer } from 'common/types'
import { format } from 'date-fns'
import { DATE_FORMAT_LONG, formatMs } from 'common/api/v1/helpers'
import Error from '@mui/icons-material/Error'
import capitalize from '@mui/utils/capitalize'
import Divider from '@mui/material/Divider'
import { AdAuditLogRequestParams, AdCreativesRequestParams } from '../../../../api/nm-types'
import { withDefaultPagination } from '../../../../utils'
import Stack from '@mui/material/Stack'
import Collapse from '@mui/material/Collapse'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import IconButton from '@mui/material/IconButton'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import codeTheme from '../../../../themes/code'
import { RecursiveDataSet } from '../../../common/RecursiveDataSet'
import { Link } from '../../../common/Link'
import Tooltip from '../../../common/Tooltip'
import Skeleton from '@mui/material/Skeleton'
import { splitPlaylistAdId } from 'common/adInsertion'

type AuditColumn = TableConfig<AdAuditLogMessage>[number]

export const AuditSection = ({
  dispatch,
  adBreak,
  adCreatives,
  adPod,
  adServer,
  refreshTrigger,
}: {
  dispatch: AppDispatch
  adBreak: AdBreak
  adCreatives: AdCreativeWithUrls[]
  adPod: AdPod | undefined
  adServer: AdServer | undefined
  refreshTrigger?: number
}) => {
  const { adAudit, total } = useSelector(
    ({ adAuditReducer }: GlobalState) => ({
      adAudit: adAuditReducer.adAudit,
      total: adAuditReducer.total,
    }),
    equals,
  )
  const [searchParams, setSearchParams] = useState<AdAuditLogRequestParams>(
    withDefaultPagination({ rowsPerPage: '30' } as AdCreativesRequestParams),
  )
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [expandedRawJson, setExpandedRawJson] = useState<Set<number>>(new Set())

  useEffect(() => {
    dispatch(getAdAudit({ ...searchParams, adBreakId: adBreak.id }))
  }, [dispatch, searchParams, adBreak.id, refreshTrigger])

  useEffect(() => {
    setExpandedRows(new Set())
    setExpandedRawJson(new Set())
  }, [searchParams.pageNumber])

  const handleExpandClick = (rowIdx: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      next.has(rowIdx) ? next.delete(rowIdx) : next.add(rowIdx)
      return next
    })
  }

  const handleRawJsonExpandClick = (rowIdx: number) => {
    setExpandedRawJson((prev) => {
      const next = new Set(prev)
      next.has(rowIdx) ? next.delete(rowIdx) : next.add(rowIdx)
      return next
    })
  }

  const getAdCreative = (creativeId: string): AdCreativeWithUrls | undefined => {
    return adCreatives.find((creative) => creative.id === creativeId)
  }

  const getAdPod = (): AdPod | undefined => {
    return adPod
  }

  const getAdServerById = (): AdServer | undefined => {
    return adServer
  }

  const isEntityObject = (entity: any): boolean => {
    if (!entity) return false
    try {
      const parsed = JSON.parse(entity)
      return typeof parsed === 'object' && parsed !== null
    } catch {
      return false
    }
  }

  const tableConfig = [
    {
      title: '',
      getValue: (adAudit, i) => {
        if (!adAudit.entity && !adAudit.message) {
          return null
        }

        const enrichedEntity = enrichEntity(adAudit, getAdCreative, getAdPod, getAdServerById)
        if (!enrichedEntity) {
          return null
        }

        return (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              handleExpandClick(i)
            }}
            aria-label="expand row"
            // Add custom styling to make it more compact
            sx={{
              padding: '2px',
              margin: '-6px',
            }}
          >
            {expandedRows.has(i) ? (
              <KeyboardArrowDownIcon fontSize="small" />
            ) : (
              <KeyboardArrowRightIcon fontSize="small" />
            )}
          </IconButton>
        )
      },
      props: { sx: { width: 36, padding: 'none' }, align: 'center' },
    },
    {
      title: 'status',
      getValue: statusFromAudit,
      props: {
        sx: { width: '30px' },
      },
    },

    {
      title: 'entityType',
      getValue: (adAudit) => {
        return (
          <Typography component="div" variant="body2" data-cell-name="entityType-cell">
            {capitalize(entityTypeToNoun(adAudit.entityType))}
          </Typography>
        )
      },
    },
    {
      title: 'message',
      getValue: readableFromAudit,
    },
    {
      title: 'context',
      getValue: (adAudit) => (
        <Typography
          component="div"
          variant="body2"
          sx={{
            maxWidth: '30vw',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {adAudit.message}
        </Typography>
      ),
      props: {
        sx: { maxWidth: '32vw' },
      },
    },
    {
      title: 'component',
      getValue: (adAudit) => adAudit.component,
    },
    {
      title: 'timestamp',
      getValue: (adAudit) => format(adAudit.timestamp, DATE_FORMAT_LONG),
    },
  ] as AuditColumn[]

  return (
    <Paper title={'Audit'}>
      <Stack direction={'column'} sx={{ height: '50vh', flexGrow: 1, display: 'flex' }}>
        <Box sx={{ flexGrow: 1, overflowY: 'auto', borderRadius: '4px' }}>
          {adAudit?.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography>No audit events.</Typography>
            </Box>
          ) : (
            <MuiTable size="small">
              <TableBody>
                {adAudit.map((adAudit, i) => {
                  const enrichedEntity = enrichEntity(adAudit, getAdCreative, getAdPod, getAdServerById)
                  return (
                    <Fragment key={i}>
                      <TableRow hover>
                        {tableConfig
                          .filter((c) => c !== null)
                          .map(({ getValue, props, title }) => (
                            <TableCell {...props} data-cell-name={`${title}-cell`} key={`${i}-${title}`}>
                              <Typography component="div" variant="body2">
                                {getValue(adAudit, i)}
                              </Typography>
                            </TableCell>
                          ))}
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={tableConfig.length} sx={{ p: 0, border: 0 }}>
                          <Collapse in={expandedRows.has(i)} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2 }}>
                              {enrichedEntity && <RecursiveDataSet object={enrichedEntity} sentenceCase />}
                              {isEntityObject(adAudit.entity) && (
                                <>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      cursor: 'pointer',
                                      mt: 2,
                                      ml: -1,
                                      borderRadius: 1,
                                    }}
                                    onClick={() => handleRawJsonExpandClick(i)}
                                  >
                                    <IconButton
                                      size="small"
                                      aria-label="expand raw json"
                                      sx={{ padding: '2px', marginRight: '4px' }}
                                    >
                                      {expandedRawJson.has(i) ? (
                                        <KeyboardArrowDownIcon fontSize="small" />
                                      ) : (
                                        <KeyboardArrowRightIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                    <Typography variant="body1">View raw (JSON)</Typography>
                                  </Box>
                                  <Collapse in={expandedRawJson.has(i)} timeout="auto" unmountOnExit>
                                    <Box sx={{}}>
                                      <SyntaxHighlighter
                                        language="json"
                                        style={codeTheme}
                                        lineProps={{ style: { wordBreak: 'break-all', whiteSpace: 'pre-wrap' } }}
                                        codeTagProps={{
                                          style: { backgroundColor: 'transparent' },
                                        }}
                                        wrapLines
                                        customStyle={{
                                          backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                          border: '1px solid transparent',
                                          maxWidth: '100%',
                                        }}
                                      >
                                        {formatEntity(adAudit.entity)}
                                      </SyntaxHighlighter>
                                    </Box>
                                  </Collapse>
                                </>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  )
                })}
              </TableBody>
            </MuiTable>
          )}
        </Box>
        <Box sx={{ flexShrink: 0, mt: 0 }}>
          <Divider />
          <Pagination
            id="ad-audit-pagination"
            total={total}
            page={searchParams.pageNumber || '0'}
            perPage={searchParams.rowsPerPage || '30'}
            changePageCallback={(page: string) => {
              setSearchParams((prev) => ({ ...prev, pageNumber: page }))
            }}
            changeRowsPerPageCallback={(rowsPerPage: string) => {
              setSearchParams((prev) => ({ ...prev, pageNumber: '0', rowsPerPage }))
            }}
          />
        </Box>
      </Stack>
    </Paper>
  )
}

const statusFromAudit = (adAudit: AdAuditLogMessage): ReactNode => {
  switch (adAudit.status) {
    case 'start':
      return null
    case 'finish':
      // If "finish" but result is "fail" or "warning", it means the operation probably led to extra work, such as transcoding
      if (adAudit.result === 'fail' || adAudit.result === 'warning') {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Error sx={{ color: 'white', ml: 0.5 }} />
          </Box>
        )
      }
      return null
    case 'fail':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Error sx={{ color: 'error.main' }} />
        </Box>
      )
    case 'warning':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Error sx={{ color: 'warning.main' }} />
        </Box>
      )

    default:
      return null
  }
}

/**
 * Enriches the entity of AdAuditLogMessage with additional properties, such as links instead of IDs for certain
 * entity types. It is used before displaying the audit log to provide more context.
 *
 * Returns undefined if the entity is empty or does not match any known entity type.
 */
const enrichEntity = (
  adAuditIn: AdAuditLogMessage,
  getCreative: (creativeId: string) => AdCreativeWithUrls | undefined,
  getPod: (podId: string) => AdPod | undefined,
  getAdServer: (adServerId: string) => AdServer | undefined,
): any => {
  if (!adAuditIn.entity) {
    return adAuditIn.entity
  }

  const tryJson = (value: any): any => {
    try {
      return JSON.parse(value)
    } catch (e) {
      return {
        context: value,
      }
    }
  }

  let adAuditOut = tryJson(adAuditIn.entity)
  if (adAuditIn.message) {
    adAuditOut = { message: adAuditIn.message, ...adAuditOut }
  }

  // Ad break ID does not make sense to show here, since we are in the ad break already
  delete adAuditOut.adBreakId
  // Stream ID and channel ID are internal IDs
  delete adAuditOut.streamId
  delete adAuditOut.channelId

  switch (adAuditIn.entityType) {
    case AdAuditEntity.impressionErrorReport:
    case AdAuditEntity.impressionReport: {
      const splits = splitPlaylistAdId(adAuditOut.creativeId)

      if (splits) {
        const { adServerId, isBuiltIn, creativeId } = splits

        if (isBuiltIn) {
          const creative = getCreative(creativeId)
          adAuditOut.creative = creative ? (
            <Link to={`/adInsertion/adCreatives/update/${creativeId}`} underline="hover">
              {creative.name}
            </Link>
          ) : (
            <Skeleton variant="text" width={100} height={20} />
          )
          adAuditOut.adServer = 'Built-in'
          delete adAuditOut.creativeId
        } else {
          const adServer = getAdServer(adServerId)
          adAuditOut.creativeId = creativeId
          adAuditOut.adServer = adServer ? (
            <Link to={`/adInsertion/adServers/update/${adServerId}`} underline="hover">
              {adServer.name}
            </Link>
          ) : (
            <Skeleton variant="text" width={100} height={20} />
          )
        }
        adAuditOut.timestamp = format(adAuditOut.timestamp, DATE_FORMAT_LONG)
        delete adAuditOut.url
      }
      break
    }
    case AdAuditEntity.adToTranscode: {
      switch (adAuditIn.method) {
        case AdAuditMethod.receive: {
          adAuditOut.duration = (
            <Tooltip title={`${adAuditOut.durationMs} ms`}>
              <Typography variant="body2">{formatMs(adAuditOut.durationMs)}</Typography>
            </Tooltip>
          )

          delete adAuditOut.durationMs
          delete adAuditOut.errorUrl
          break
        }
        case AdAuditMethod.parse: {
          adAuditOut.fileName = adAuditOut.context
          delete adAuditOut.context
          break
        }
      }

      break
    }
    case AdAuditEntity.mezzanineFile:
    case AdAuditEntity.transcodedFile: {
      switch (adAuditIn.method) {
        case AdAuditMethod.transcode:
        case AdAuditMethod.upload:
        case AdAuditMethod.download:
        case AdAuditMethod.exists:
        case AdAuditMethod.retentionDelete: {
          adAuditOut.fileName = adAuditOut.context
          delete adAuditOut.context
          break
        }
      }
      break
    }
    case AdAuditEntity.scte35Notification: {
      switch (adAuditIn.method) {
        case 'receive': {
          adAuditOut.duration = (
            <Tooltip title={`${adAuditOut.durationMs} ms`}>
              <Typography variant="body2">{formatMs(adAuditOut.durationMs)}</Typography>
            </Tooltip>
          )
          adAuditOut.startsAt = format(adAuditOut.startsAt, DATE_FORMAT_LONG)
          delete adAuditOut.durationMs

          break
        }
      }

      break
    }
    case AdAuditEntity.vast: {
      switch (adAuditIn.method) {
        case AdAuditMethod.parse:
        case AdAuditMethod.download: {
          const pod = getPod(adAuditOut.adPod)
          adAuditOut.adPod = pod ? (
            <Link to={`/adInsertion/adPods/update/${pod.id}`} underline="hover">
              {pod.name}
            </Link>
          ) : (
            <Skeleton variant="text" width={100} height={20} />
          )

          delete adAuditOut.adPodId

          break
        }
      }
      break
    }
  }

  if (Object.keys(adAuditOut).length === 0) {
    return undefined
  }

  return adAuditOut
}

/**
 * Convert AdAuditLogMessage to a human-readable string.
 * For most audit entries, this is auto-generated based on the entity type and method in {@link readableFallback}.
 * But this method has some overrides for specific entity types to provide more meaningful messages.
 */
const readableFromAudit = (adAudit: AdAuditLogMessage): string => {
  switch (adAudit.entityType) {
    case AdAuditEntity.adBreak:
      switch (adAudit.method) {
        case AdAuditMethod.scheduled:
          return `Ad break scheduled`
        case AdAuditMethod.canceled:
          return `Ad break canceled`
        case AdAuditMethod.aborted:
          return `Ad break aborted`
        case AdAuditMethod.play:
          return `Ad break started`
        case AdAuditMethod.end:
          return `Ad break ended`
      }
    case AdAuditEntity.applianceAd:
      switch (adAudit.method) {
        case AdAuditMethod.play:
          switch (adAudit.status) {
            case AdAuditStatus.start:
              return `Started to play ad`
            case AdAuditStatus.finish:
              return `Finished playing ad`
            case AdAuditStatus.fail:
              return `Failed to play ad`
          }
      }
    case AdAuditEntity.transcodedFile:
      switch (adAudit.method) {
        case AdAuditMethod.exists:
          return `Checked S3 storage for transcoded file`
      }
  }

  return readableFallback(adAudit)
}

const readableFallback = (adAudit: AdAuditLogMessage): string => {
  const noun = entityTypeToNoun(adAudit.entityType)
  if (adAudit.status === AdAuditStatus.fail) {
    const verb = methodToInfinitive(adAudit.method)
    return `Failed to ${verb}`
  }

  if (adAudit.status === AdAuditStatus.warning) {
    const verb = methodToPastTense(adAudit.method)
    return `${capitalize(noun)} ${verb}`
  }

  if (adAudit.status === AdAuditStatus.start) {
    const verb = methodToInfinitive(adAudit.method)
    return `Started to ${verb} ${noun}`
  }

  const verb = methodToPastTense(adAudit.method)

  return `${capitalize(verb)} ${noun}`
}

const entityTypeToNoun = (entityType: AdAuditEntity): string => {
  switch (entityType) {
    case AdAuditEntity.impressionReport:
      return 'impression report'
    case AdAuditEntity.impressionErrorReport:
      return 'impression error report'
    case AdAuditEntity.vast:
      return 'VAST'
    case AdAuditEntity.scte35Notification:
      return 'SCTE-35 notification'
    case AdAuditEntity.applianceAdPlaylist:
      return 'appliance ad playlist'
    case AdAuditEntity.adToTranscode:
      return 'ad to transcode'
    case AdAuditEntity.transcodedFile:
      return 'transcoded file'
    case AdAuditEntity.mezzanineFile:
      return 'mezzanine file'
    case AdAuditEntity.applianceAd:
      return 'appliance ad'
    case AdAuditEntity.adBreak:
      return 'ad break'
    case AdAuditEntity.outputStream:
      return 'output stream'
    default:
      return 'unknown entity'
  }
}

const methodToPastTense = (method?: AdAuditMethod): string => {
  if (!method) {
    return ''
  }

  switch (method) {
    case AdAuditMethod.receive:
      return 'received'
    case AdAuditMethod.download:
      return 'downloaded'
    case AdAuditMethod.upload:
      return 'uploaded'
    case AdAuditMethod.transcode:
      return 'transcoded'
    case AdAuditMethod.parse:
      return 'parsed'
    case AdAuditMethod.request:
      return 'requested'
    case AdAuditMethod.retentionDelete:
      return 'deleted'
    case AdAuditMethod.play:
      return 'played'
    case AdAuditMethod.end:
      return 'ended'
    case AdAuditMethod.scheduled:
      return 'scheduled'
    case AdAuditMethod.canceled:
      return 'canceled'
    case AdAuditMethod.aborted:
      return 'aborted'
    case AdAuditMethod.exists:
      return 'checked'
    default:
      return ''
  }
}

const methodToInfinitive = (method?: AdAuditMethod): string => {
  if (!method) {
    return ''
  }

  switch (method) {
    case AdAuditMethod.receive:
      return 'receive'
    case AdAuditMethod.download:
      return 'download'
    case AdAuditMethod.upload:
      return 'upload'
    case AdAuditMethod.transcode:
      return 'transcode'
    case AdAuditMethod.parse:
      return 'parse'
    case AdAuditMethod.request:
      return 'request'
    case AdAuditMethod.retentionDelete:
      return 'delete'
    case AdAuditMethod.play:
      return 'play'
    case AdAuditMethod.end:
      return 'end'
    case AdAuditMethod.scheduled:
      return 'schedule'
    case AdAuditMethod.canceled:
      return 'cancel'
    case AdAuditMethod.aborted:
      return 'abort'
    case AdAuditMethod.exists:
      return 'check'
    default:
      return ''
  }
}

const formatEntity = (entity: any) => {
  try {
    return JSON.stringify(JSON.parse(entity), null, 2)
  } catch (_) {
    return entity
  }
}
